import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { Box, Container, Typography, Grid, CssBaseline, Alert } from '@mui/material';
import DashboardPhysioNavbar from './DashboardPhysioNavbar';
import NewPatientsCard from './StatCards/NewPatientsCards';
import TotalUsersCard from './StatCards/TotalUsersCard';
import ProgressLevelCard from './StatCards/ProgressLevelCard';
import PatientTable from './PatientSection/PatientTable';
import PatientDetailDrawer from './PatientSection/PatientDetailDrawer';
import { useTherapistHistoryQuery } from '../stores/tanstack/queries/useTherapistHistoryQuery';
import { useAuthStore } from '../stores/zustand';

const DashboardPhysio = () => {
  const [selectedPatient, setSelectedPatient] = useState(null);
  const { t } = useTranslation();

  // get user info from auth store as we have zustand/authStore
  const user = useAuthStore((state) => state.user);
  const teamName = user?.name || user?.email?.split('@')[0] || t('team');
  // fetch patients data from API from tanstack useTherapistHistoryQuery hook
  const { clients, isLoading, error, refetch } = useTherapistHistoryQuery();

  // status based logic (using API status field)
  const dashboardData = useMemo(() => {
    if (!clients?.length) return {
      teamName,
      newPatientsThisWeek: 0,
      totalActivePatients: 0,
      totalUsers: 0,
      currentLevel: 1,
      patientsUntilNextLevel: 10,
    };

    let newPatientsThisWeek = 0;
    // count patients by status
    const statusCounts = {
      NEW: 0,
      ACTIVE: 0,
      INACTIVE: 0,
    };

    clients.forEach(client => {
      statusCounts[client.status] = (statusCounts[client.status] || 0) + 1;

      // New patients = NEW status
      if (client.status === 'NEW') {
        newPatientsThisWeek++;
      }
    });

    const totalUsers = clients.length;
    const totalActivePatients = statusCounts.ACTIVE;
    const currentLevel = Math.floor(totalUsers / 10) + 1;
    // patients needed until next level (showing progress towards next level)
    const patientsUntilNextLevel = Math.max(0, (currentLevel * 10) - totalUsers);

    return {
      teamName,
      newPatientsThisWeek,
      totalActivePatients,
      totalUsers,
      currentLevel,
      patientsUntilNextLevel,
      statusCounts,
    };
  }, [clients, teamName]);

  // Theme setup
  const theme = createTheme({
    palette: {
      mode: 'dark',
      primary: { main: '#4F46E5' },
      secondary: { main: '#10B981' },
      background: { default: '#0a0e27', paper: '#1a1f3a' },
      success: { main: '#10B981', light: '#D1FAE5' },
      grey: { 600: '#6B7280' },
    },
    typography: {
      fontFamily: '"Inter", "Roboto", sans-serif',
      h1: { fontSize: '2rem', fontWeight: 700 },
    },
    shape: { borderRadius: 12 },
  });

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ minHeight: '100vh', background: '#02024a' }}>
        {/* Navbar Section */}
        <DashboardPhysioNavbar />

        <Container maxWidth="xl" sx={{ py: 4 }}>
          {/* Welcome Section */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h1" sx={{ color: 'white', mb: 1 }}>
              {t('welcomeBack')}, {dashboardData.teamName}
            </Typography>
            <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
              {t('currentOverview')}
            </Typography>
          </Box>

          {/* Error Alert */}
          {error && (
            <Alert
              severity="error"
              sx={{ mb: 3, borderRadius: 2 }}>
              {t('errorLoadingData')}: {error.message}
            </Alert>
          )}

          {/* Status based Stats Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }} justifyContent="center">
            <Grid item xs={12} md={4}>
              <NewPatientsCard
                count={dashboardData.newPatientsThisWeek}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TotalUsersCard
                count={dashboardData.totalActivePatients}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <ProgressLevelCard
                currentLevel={dashboardData.currentLevel}
                patientsUntilNextLevel={dashboardData.patientsUntilNextLevel}
              />
            </Grid>
          </Grid>

          {/* Patient Table Section */}
          <PatientTable
            onPatientClick={setSelectedPatient}
            onPatientAdded={refetch}
          />

          {/* Patient Detail Drawer */}
          <PatientDetailDrawer
            patient={selectedPatient}
            open={Boolean(selectedPatient)}
            onClose={() => setSelectedPatient(null)}
          />
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default DashboardPhysio;
